import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Key, Globe, Thermometer, Hash, Bot, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../supabase/client';

interface AIConfig {
  id: string;
  api_key: string;
  base_url: string;
  model: string;
  temperature: number;
  max_tokens: number;
  created_at?: string;
  updated_at?: string;
}

export default function AISettings() {
  const [config, setConfig] = useState<AIConfig>({
    id: 'default',
    api_key: 'ark-6bbd0f1c-4bac-463a-ba34-7b4629057865-37c4',
    base_url: 'https://ark.cn-beijing.volces.com/api/v3',
    model: 'Doubao-Seed-2.0-Code',
    temperature: 0.7,
    max_tokens: 2000
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('ai_config')
        .select('*')
        .eq('id', 'default')
        .single();

      if (error && error.code !== 'PGRST116') {
        
        return;
      }

      if (data) {
        setConfig({
          id: data.id,
          api_key: data.api_key || '',
          base_url: data.base_url || '',
          model: data.model || 'qwen3.6-plus',
          temperature: data.temperature ?? 0.7,
          max_tokens: data.max_tokens ?? 2000
        });
      }
    } catch (e) {
      
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const { error } = await supabase
        .from('ai_config')
        .upsert({
          id: 'default',
          api_key: config.api_key,
          base_url: config.base_url,
          model: config.model,
          temperature: config.temperature,
          max_tokens: config.max_tokens,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        });

      if (error) {
        setMessage({ type: 'error', text: '保存失败: ' + error.message });
        return;
      }

      setMessage({ type: 'success', text: '配置保存成功！' });
      setTimeout(() => setMessage(null), 3000);
    } catch (e: any) {
      setMessage({ type: 'error', text: '保存异常: ' + (e.message || '未知错误') });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('code-grading', {
        body: {
          action: 'ai_test',
          testMessage: 'test'
        }
      });

      if (error) {
        setMessage({ type: 'error', text: '连接测试失败: ' + error.message });
        return;
      }

      setMessage({ type: 'success', text: '连接测试成功！AI服务可用' });
    } catch (e: any) {
      setMessage({ type: 'error', text: '连接测试异常: ' + (e.message || '未知错误') });
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bot className="w-7 h-7 text-blue-600" />
          AI配置
        </h1>
        <p className="text-gray-500 mt-1">配置豆包大模型API参数，统一管理AI阅卷、AI讲题、试卷解析等功能</p>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </motion.div>
      )}

      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Key className="w-4 h-4 inline mr-1" />
              API密钥
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={config.api_key}
                onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
                placeholder="请输入豆包大模型API密钥"
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
              />
              <button
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">API密钥将加密存储，仅用于后端调用</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="w-4 h-4 inline mr-1" />
              接口地址
            </label>
            <input
              type="text"
              value={config.base_url}
              onChange={(e) => setConfig({ ...config, base_url: e.target.value })}
              placeholder="https://api.example.com"
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Bot className="w-4 h-4 inline mr-1" />
              模型名称
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={config.model}
                onChange={(e) => setConfig({ ...config, model: e.target.value })}
                placeholder="输入或选择模型ID"
                className="flex-1 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    setConfig({ ...config, model: e.target.value });
                  }
                }}
                className="px-4 py-2.5 bg-gray-50 border rounded-lg text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer min-w-[140px]"
              >
                <option value="">快速选择</option>
                <option value="Doubao-Seed-2.0-Code">Doubao-Seed-2.0-Code</option>
                <option value="doubao-lite-4k">doubao-lite-4k</option>
                <option value="doubao-pro-4k">doubao-pro-4k</option>
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">可从下拉框快速选择常用模型，或在输入框中手动输入其他模型ID</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Thermometer className="w-4 h-4 inline mr-1" />
                温度 (Temperature)
              </label>
              <input
                type="number"
                min="0"
                max="2"
                step="0.1"
                value={config.temperature}
                onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) })}
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">控制生成文本的随机性，0-2之间</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Hash className="w-4 h-4 inline mr-1" />
                最大生成长度
              </label>
              <input
                type="number"
                min="100"
                max="8000"
                step="100"
                value={config.max_tokens}
                onChange={(e) => setConfig({ ...config, max_tokens: parseInt(e.target.value) })}
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">单次请求最大token数</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-8 pt-6 border-t">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
          >
            <Save className="w-5 h-5" />
            {saving ? '保存中...' : '保存配置'}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={testConnection}
            className="flex items-center gap-2 px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            <Bot className="w-5 h-5" />
            测试连接
          </motion.button>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h3 className="font-medium text-blue-800 mb-2">配置说明</h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>API密钥用于后端调用豆包大模型，不会在前端暴露</li>
          <li>配置保存后，AI阅卷、AI讲题、试卷解析等功能将自动使用此配置</li>
          <li>温度参数越低，AI回答越确定；越高则越具创造性</li>
          <li>建议保持默认模型 qwen3.6-plus 以获得最佳效果</li>
        </ul>
      </div>
    </div>
  );
}
